import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Grid,
  Avatar,
  Divider
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
  Inventory as InventoryIcon,
  Image as ImageIcon,
  AttachMoney as AttachMoneyIcon
} from '@mui/icons-material';

const CatalogSendPreview = ({ singleCatalog, platform }) => {
  if (!singleCatalog) {
  return (
    <Card sx={{ maxWidth: 350, m: 0.5 }}>
      <CardContent sx={{ p: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          No catalog selected
        </Typography>
      </CardContent>
    </Card>
  );
  }

  const { name, description, products = [], type = 'single' } = singleCatalog;

  const renderProduct = (product, index) => {
    const {
      name: productName,
      description: productDesc,
      price,
      currency = 'USD',
      availability = 'in stock',
      image_url,
      id
    } = product;

    // Validate image URL
    const isValidImageUrl = image_url && 
      image_url.trim() !== '' && 
      image_url !== 'null' && 
      image_url !== 'undefined' &&
      (image_url.startsWith('http://') || image_url.startsWith('https://'));

    const getAvailabilityColor = (status) => {
      switch (status?.toLowerCase()) {
        case 'in stock':
          return 'success';
        case 'out of stock':
          return 'error';
        case 'preorder':
          return 'warning';
        default:
          return 'default';
      }
    };

    return (
      <Box key={id || index} sx={{ mb: 1.5 }}>
        <Grid container spacing={1.5} alignItems="center">
          {/* Product Image */}
          <Grid item xs={3}>
            <Avatar
              src={isValidImageUrl ? image_url : undefined}
              variant="rounded"
              sx={{ 
                width: 45, 
                height: 45,
                bgcolor: 'grey.200'
              }}
              imgProps={{
                onError: (e) => {
                  e.target.style.display = 'none';
                }
              }}
            >
              {!isValidImageUrl && <ImageIcon sx={{ fontSize: 20 }} />}
            </Avatar>
          </Grid>

          {/* Product Details */}
          <Grid item xs={9}>
            <Typography variant="subtitle2" fontWeight="bold" noWrap sx={{ fontSize: '0.85rem' }}>
              {productName || 'Unnamed Product'}
            </Typography>
            
            {productDesc && (
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  mb: 0.5,
                  fontSize: '0.75rem'
                }}
              >
                {productDesc}
              </Typography>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {price && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AttachMoneyIcon sx={{ fontSize: 14, color: 'success.main' }} />
                  <Typography variant="body2" fontWeight="bold" color="success.main" sx={{ fontSize: '0.8rem' }}>
                    {price} {currency}
                  </Typography>
                </Box>
              )}
              
              <Chip
                label={availability}
                size="small"
                color={getAvailabilityColor(availability)}
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <Card sx={{ maxWidth: 350, m: 0.5, border: '1px solid', borderColor: 'primary.main' }}>
      <CardContent sx={{ p: 1.5 }}>
        {/* Catalog Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <ShoppingCartIcon color="primary" sx={{ fontSize: 20 }} />
          <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: '0.95rem' }}>
            {name || 'Catalog'}
          </Typography>
          <Chip 
            label={type === 'single' ? 'Single' : 'Multi'} 
            size="small" 
            color="primary" 
            variant="outlined"
            sx={{ fontSize: '0.7rem', height: 20 }}
          />
        </Box>

        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.8rem' }}>
            {description}
          </Typography>
        )}

        <Divider sx={{ mb: 1.5 }} />

        {/* Products Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <InventoryIcon color="action" sx={{ fontSize: 18 }} />
          <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: '0.85rem' }}>
            Products ({products.length})
          </Typography>
        </Box>

        {products.length > 0 ? (
          <Box sx={{ maxHeight: 150, overflowY: 'auto' }}>
            {products.slice(0, 2).map((product, index) => renderProduct(product, index))}
            {products.length > 2 && (
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.75rem', display: 'block', mt: 1 }}>
                ... and {products.length - 2} more products
              </Typography>
            )}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.8rem' }}>
            No products available
          </Typography>
        )}

        {/* Footer */}
        <Divider sx={{ mt: 1.5, mb: 0.5 }} />
        <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ fontSize: '0.7rem' }}>
          {platform === 'instagram' ? 'Instagram catalog preview' : platform === 'facebook' ? 'Facebook catalog preview' : 'WhatsApp Catalog Message Preview'}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default CatalogSendPreview;
